import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Trash2, Paperclip, ChevronDown, Search, X, Check } from 'lucide-react';
import { adminAPI } from '../services/adminApi';
import './AdminFeedback.css';

const STATUS_OPTIONS = [
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'need_info', label: 'Need Info' },
    { value: 'wont_fix', label: "Won't Fix" },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map(s => [s.value, s.label]));

const StatusSelect = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
    const wrapRef = useRef(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const handleToggle = () => {
        if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            
            setPos({ top: rect.bottom + 6, left: rect.left, width: rect.width });
        }
        setOpen(o => !o);
    };

    
    useLayoutEffect(() => {
        if (!open || !btnRef.current || !menuRef.current) return;
        const btnRect = btnRef.current.getBoundingClientRect();
        const menuRect = menuRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        let top = btnRect.bottom + 6;
        if (top + menuRect.height > viewportHeight - 8) {
            const aboveTop = btnRect.top - menuRect.height - 6;
            top = aboveTop > 8 ? aboveTop : Math.max(8, viewportHeight - menuRect.height - 8);
        }

        let left = btnRect.left;
        if (left + btnRect.width > viewportWidth - 8) {
            left = Math.max(8, viewportWidth - btnRect.width - 8);
        }

        setPos({ top, left, width: btnRect.width });
    }, [open]);

    const selected = STATUS_OPTIONS.find(s => s.value === value);

    return (
        <div className="fb-status-select" ref={wrapRef}>
            <button ref={btnRef} type="button" className="fb-status-select-btn" onClick={handleToggle}>
                <span>{selected ? selected.label : 'Select status'}</span>
                <ChevronDown size={14} />
            </button>
            {open && (
                <div
                    className="fb-status-select-menu"
                    ref={menuRef}
                    style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width }}
                >
                    <div className="fb-status-select-list">
                        {STATUS_OPTIONS.map(s => (
                            <div
                                key={s.value}
                                className={`fb-status-select-item ${value === s.value ? 'selected' : ''}`}
                                onClick={() => { onChange(s.value); setOpen(false); }}
                            >
                                {s.label}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const StatusEditor = ({ entry, onSaved }) => {
    const [open, setOpen] = useState(false);
    const [status, setStatus] = useState(entry.status || 'open');
    const [note, setNote] = useState(entry.admin_note || '');
    const [saving, setSaving] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const btnRef = useRef(null);
    const popRef = useRef(null);

    useEffect(() => {
        const onClick = (e) => {
            if (popRef.current?.contains(e.target)) return;
            if (btnRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const handleToggle = () => {
        if (!open && btnRef.current) {
            const rect = btnRef.current.getBoundingClientRect();
            setPos({ top: rect.bottom + 6, left: Math.min(rect.left, window.innerWidth - 300) });
        }
        setOpen(o => !o);
    };

   
    useLayoutEffect(() => {
        if (!open || !btnRef.current || !popRef.current) return;
        const btnRect = btnRef.current.getBoundingClientRect();
        const popRect = popRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        let top = btnRect.bottom + 6;
        if (top + popRect.height > viewportHeight - 8) {
            const aboveTop = btnRect.top - popRect.height - 6;
            top = aboveTop > 8 ? aboveTop : Math.max(8, viewportHeight - popRect.height - 8);
        }

        let left = Math.min(btnRect.left, viewportWidth - popRect.width - 8);
        left = Math.max(8, left);

        setPos({ top, left });
    }, [open]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await adminAPI.updateFeedbackStatus(entry.report_id, { created_at: entry.created_at, status, note });
            setOpen(false);
            onSaved();
        } catch { /* noop */ }
        finally { setSaving(false); }
    };

    const currentStatus = entry.status || 'open';

    return (
        <div className="fb-status-wrap">
            <button
                ref={btnRef}
                type="button"
                className={`fb-status-badge fb-status-${currentStatus}`}
                onClick={handleToggle}
                title={entry.admin_note || 'Set status'}
            >
                {STATUS_LABEL[currentStatus] || 'Open'}
            </button>
            {open && (
                <div className="fb-status-popover" ref={popRef} style={{ position: 'fixed', top: pos.top, left: pos.left }}>
                    <div className="fb-status-field">
                        <label>Status</label>
                        <StatusSelect value={status} onChange={setStatus} />
                    </div>
                    <div className="fb-status-field">
                        <label>Note to user (optional)</label>
                        <textarea
                            rows={3}
                            placeholder="e.g. Fixed in latest release"
                            value={note}
                            onChange={e => setNote(e.target.value)}
                        />
                    </div>
                    <div className="fb-status-actions">
                        <button type="button" className="fb-status-cancel" onClick={() => setOpen(false)} disabled={saving}>Cancel</button>
                        <button type="button" className="fb-status-save" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : <><Check size={13} /> Save &amp; notify</>}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const UserFilterDropdown = ({ users, value, onChange }) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const ref = useRef(null);


    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const selectedUser = users.find(u => u.id === value);
    const filtered = users.filter(u => {
        const q = query.trim().toLowerCase();
        if (!q) return true;
        return (u.first_name || '').toLowerCase().includes(q) || (u.last_name || '').toLowerCase().includes(q);
    });

    return (
        <div className="fb-user-filter" ref={ref}>
            <button type="button" className="fb-user-filter-btn" onClick={() => setOpen(o => !o)}>
                <span>{selectedUser ? `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim() : 'All Users'}</span>
                {selectedUser ? (
                    <span
                        className="fb-user-filter-clear"
                        onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); setQuery(''); }}
                    >
                        <X size={13} />
                    </span>
                ) : (
                    <ChevronDown size={14} />
                )}
            </button>
            {open && (
                <div className="fb-user-filter-menu">
                    <div className="fb-user-filter-search">
                        <Search size={13} />
                        <input autoFocus placeholder="Search by username" value={query} onChange={e => setQuery(e.target.value)} />
                    </div>
                    <div className="fb-user-filter-list">
                        <div
                            className={`fb-user-filter-item ${!value ? 'selected' : ''}`}
                            onClick={() => { onChange(''); setOpen(false); setQuery(''); }}
                        >
                            All Users
                        </div>
                        {filtered.map(u => (
                            <div
                                key={u.id}
                                className={`fb-user-filter-item ${value === u.id ? 'selected' : ''}`}
                                onClick={() => { onChange(u.id); setOpen(false); setQuery(''); }}
                            >
                                {`${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email}
                            </div>
                        ))}
                        {filtered.length === 0 && <div className="fb-user-filter-empty">No users found</div>}
                    </div>
                </div>
            )}
        </div>
    );
};

const StatusFilterDropdown = ({ value, onChange }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const selected = STATUS_OPTIONS.find(s => s.value === value);

    return (
        <div className="fb-user-filter" ref={ref}>
            <button type="button" className="fb-user-filter-btn" onClick={() => setOpen(o => !o)}>
                <span>{selected ? selected.label : 'All Statuses'}</span>
                {selected ? (
                    <span
                        className="fb-user-filter-clear"
                        onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }}
                    >
                        <X size={13} />
                    </span>
                ) : (
                    <ChevronDown size={14} />
                )}
            </button>
            {open && (
                <div className="fb-user-filter-menu">
                    <div className="fb-user-filter-list">
                        <div
                            className={`fb-user-filter-item ${!value ? 'selected' : ''}`}
                            onClick={() => { onChange(''); setOpen(false); }}
                        >
                            All Statuses
                        </div>
                        {STATUS_OPTIONS.map(s => (
                            <div
                                key={s.value}
                                className={`fb-user-filter-item ${value === s.value ? 'selected' : ''}`}
                                onClick={() => { onChange(s.value); setOpen(false); }}
                            >
                                {s.label}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Pagination = ({ page, totalPages, onChange }) => {
    if (totalPages <= 1) return null;

    // Compact style: 1 … current-1 current current+1 … last — always use an
    // ellipsis for any gap, never auto-fill a single missing page number.
    const delta = 1;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
        if (i === 1 || i === totalPages || (i >= page - delta && i <= page + delta)) {
            pages.push(i);
        }
    }
    const withDots = [];
    let prev;
    for (const p of pages) {
        if (prev !== undefined && p - prev > 1) withDots.push('...');
        withDots.push(p);
        prev = p;
    }

    return (
        <div className="fb-pagination">
            <button className="fb-page-btn" disabled={page <= 1} onClick={() => onChange(page - 1)}>Previous</button>
            {withDots.map((p, i) =>
                p === '...' ? (
                    <span key={`e${i}`} className="fb-page-ellipsis">...</span>
                ) : (
                    <button
                        key={p}
                        className={`fb-page-btn fb-page-num ${p === page ? 'active' : ''}`}
                        onClick={() => onChange(p)}
                    >
                        {p}
                    </button>
                )
            )}
            <button className="fb-page-btn" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Next</button>
        </div>
    );
};


const FB_STATE_KEY = 'admin_feedback_filters';

const loadSavedFbState = () => {
    try {
        const saved = sessionStorage.getItem(FB_STATE_KEY);
        return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
};

const AdminFeedback = () => {
    const [rows, setRows] = useState([]);
    const [users, setUsers] = useState([]);
    const [pagination, setPagination] = useState({ totalPages: 1, total: 0 });
    const [loading, setLoading] = useState(true);

    const saved = loadSavedFbState();
    const [page, setPage] = useState(saved.page || 1);
    const [userId, setUserId] = useState(saved.userId || '');
    const [dateFrom, setDateFrom] = useState(saved.dateFrom || '');
    const [dateTo, setDateTo] = useState(saved.dateTo || '');
    const [status, setStatus] = useState(saved.status || '');

    useEffect(() => {
        adminAPI.getUsers({ role: 'user' }).then(r => setUsers(r.data || [])).catch(() => { });
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await adminAPI.getFeedback({
                page, limit: 10,
                user_id: userId || undefined,
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
                status: status || undefined,
            });
            setRows(data.data);
            setPagination(data.pagination);
        } catch { /* noop */ }
        finally { setLoading(false); }
    }, [page, userId, dateFrom, dateTo, status]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        try {
            sessionStorage.setItem(FB_STATE_KEY, JSON.stringify({ page, userId, dateFrom, dateTo, status }));
        } catch { /* noop */ }
    }, [page, userId, dateFrom, dateTo, status]);

    const handleDelete = async (reportId, created_at) => {
        if (!window.confirm('Delete this feedback entry?')) return;
        await adminAPI.deleteFeedback(reportId, created_at);
        load();
    };

    const formatDate = (iso) => new Date(iso).toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
    });
    const formatTime = (iso) => new Date(iso).toLocaleTimeString('en-IN', {
        hour: '2-digit', minute: '2-digit',
    });

    return (
        <div className="fb-page">
            <h1 className="fb-page-title">Feedbacks</h1>
            <div className="fb-toolbar">
                <UserFilterDropdown users={users} value={userId} onChange={v => { setUserId(v); setPage(1); }} />
                <StatusFilterDropdown value={status} onChange={v => { setStatus(v); setPage(1); }} />

                <div className="fb-date-range">
                    <input
                        type="date"
                        className="fb-date-filter"
                        value={dateFrom}
                        max={dateTo || undefined}
                        onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                    />
                    <span className="fb-date-sep">to</span>
                    <input
                        type="date"
                        className="fb-date-filter"
                        value={dateTo}
                        min={dateFrom || undefined}
                        onChange={e => { setDateTo(e.target.value); setPage(1); }}
                    />
                </div>

                {(userId || dateFrom || dateTo || status) && (
                    <button className="fb-clear-filters" onClick={() => {
                        setUserId(''); setDateFrom(''); setDateTo(''); setStatus(''); setPage(1);
                        try { sessionStorage.removeItem(FB_STATE_KEY); } catch { /* noop */ }
                    }}>
                        Clear filters
                    </button>
                )}
            </div>

            <div className="fb-table-card">
                <table className="fb-table">
                    <thead>
                        <tr>
                            <th className="fb-col-date">Date</th>
                            <th className="fb-col-user">User</th>
                            <th className="fb-col-report">Report</th>
                            <th className="fb-col-technique">Technique</th>
                            <th className="fb-col-status">Status</th>
                            <th className="fb-col-message">Message</th>
                            <th className="fb-col-attach">Attachments</th>
                            <th className="fb-col-actions"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={8} className="fb-empty-row">Loading...</td></tr>
                        )}
                        {!loading && rows.length === 0 && (
                            <tr><td colSpan={8} className="fb-empty-row">No feedback found</td></tr>
                        )}
                        {!loading && rows.map((f, i) => (
                            <tr key={`${f.report_id}-${f.created_at}-${i}`}>
                                <td className="fb-col-date">
                                    <div>{formatDate(f.created_at)}</div>
                                    <div className="fb-col-time">{formatTime(f.created_at)}</div>
                                </td>
                                <td className="fb-col-user">
                                    {f.user ? (`${f.user.first_name || ''} ${f.user.last_name || ''}`.trim() || f.user.email) : '—'}
                                </td>
                                <td className="fb-col-report">
                                    {f.report_modality && <span className="fb-chip">{f.report_modality}</span>}
                                    {f.report_body_part && <span className="fb-report-bp"> · {f.report_body_part}</span>}
                                </td>
                                <td className="fb-col-technique">{f.report_technique || '—'}</td>
                                <td className="fb-col-status">
                                    <StatusEditor entry={f} onSaved={load} />
                                </td>
                                <td className="fb-col-message" title={f.message}>{f.message}</td>
                                <td className="fb-col-attach">
                                    {(f.files || []).map((file, fi) => (
                                        <a key={fi} href={file.url} target="_blank" rel="noreferrer" className="fb-attachment-chip">
                                            <Paperclip size={12} /> {file.name.length > 12 ? file.name.slice(0, 10) + '...' : file.name}
                                        </a>
                                    ))}
                                </td>
                                <td className="fb-col-actions">
                                    <div className="fb-actions">
                                        <Link to={`/reports/${f.report_id}`} state={{ from: 'feedback' }} className="fb-icon-btn" title="View report"><Eye size={15} /></Link>
                                        <button className="fb-icon-btn danger" title="Delete" onClick={() => handleDelete(f.report_id, f.created_at)}>
                                            <Trash2 size={15} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="fb-table-footer">
                    <span className="fb-showing-text">
                        Showing {rows.length ? (page - 1) * 10 + 1 : 0} to {(page - 1) * 10 + rows.length} of {pagination.total} entries
                    </span>
                    <Pagination page={page} totalPages={pagination.totalPages} onChange={setPage} />
                </div>
            </div>
        </div>
    );
};

export default AdminFeedback;